/** @jsx jsx */
import React, { useCallback, useState, type ReactNode } from 'react';
import { css, styled, jsx } from '@compiled/react';
import PopupDI, { type ContentProps } from '@atlaskit/popup';
import { media } from '@atlaskit/primitives';
import { UsingNotificationsToImproveAdminApprovalProvider } from '@atlassian/jira-cross-flow-using-notifications-to-improve-admin-approval/src/controllers/index.tsx';
import { JSErrorBoundary as JSErrorBoundaryDI } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useExperienceStart as useExperienceStartDI } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIntl as useIntlDI } from '@atlassian/jira-intl';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useTopMenus as useTopMenusDI } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import {
	testIdConcat,
	testIdGenerate,
} from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { isMobileAndInViewPanelIterationExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useIsAnonymous as useIsAnonymousDI } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { fg } from '@atlassian/jira-feature-gating';
import { EXPERIENCE_NAVIGATION_TOP_MENU, NAVIGATION_ITEM_ID } from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { Notification as IconDI } from '../../common/ui/notification-button/index.tsx';
import { useNavigationItemAnalytics as useNavigationItemAnalyticsDI } from '../../controllers/navigation-item-analytics/main.tsx';
import { useRegisterNotificationsInCommandPalette } from './command-palette/index.tsx';
import { MAX_NOTIFICATION_COUNT } from './constants.tsx';
import messages from './messages.tsx';
import type { NotificationsProps } from './types.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const BadgeDI = lazy(
	() =>
		import(/* webpackChunkName: "atlassian-navigation.async-notification-badge" */ './badge').then(
			({ Badge }) => Badge,
		),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const MenuDI = lazy(
	() =>
		import(
			/* webpackChunkName: "atlassian-navigation.async-notification-menu-native" */ './menu'
		).then(({ Menu }) => Menu),
	{
		ssr: false,
	},
);

const Notifications = ({
	Badge = BadgeDI,
	ErrorBoundary = JSErrorBoundaryDI,
	Icon = IconDI,
	Menu = MenuDI,
	NotificationContentFrame = NotificationContentFrameDI,
	Popup = PopupDI,
	useExperienceStart = useExperienceStartDI,
	useIntl = useIntlDI,
	useIsAnonymous = useIsAnonymousDI,
	useNavigationItemAnalytics = useNavigationItemAnalyticsDI,
	useTopMenus = useTopMenusDI,
}: NotificationsProps) => {
	const testIdPrefix = testIdGenerate('secondary-actions', 'notifications');
	const { formatMessage } = useIntl();
	const isAnonymous = useIsAnonymous();
	const [isNotificationCleared, setIsNotificationCleared] = useState(false);

	const [notificationCount, setNotificationCount] = useState(0);

	const handleCountUpdate = useCallback((newCount: number): void => {
		setNotificationCount(newCount);
	}, []);

	const [isMenuOpen, { toggle: toggleMenu, off: closeMenu, on: openMenu }] = useTopMenus(
		MENU_ID.NOTIFICATIONS,
	);
	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.NOTIFICATIONS,
	});
	const onStart = useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.NOTIFICATIONS,
		analyticsSource: 'atlassian-navigation',
	});

	const onClick = useCallback(() => {
		if (isMenuOpen) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			performance.clearMarks('notification.list.render.start');
		} else {
			performance.mark('notification.list.render.start');
		}

		onStart();
		toggleMenu();
		triggerAnalytics();
		setIsNotificationCleared(true);
	}, [isMenuOpen, onStart, toggleMenu, triggerAnalytics]);

	const handleNotificationIconHover = () => {
		MenuDI.preload();
	};

	useRegisterNotificationsInCommandPalette(onClick);

	const content = useCallback(
		({ update }: ContentProps) => (
			<NotificationContentFrame>
				<ErrorBoundary
					id="notifications.list.async"
					packageName="AtlassianNavigation"
					teamName="navigation"
				>
					<Placeholder name="notification-menu-native" fallback={null}>
						<Menu scheduleUpdate={update} testIdPrefix={testIdPrefix} />
					</Placeholder>
				</ErrorBoundary>
			</NotificationContentFrame>
		),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[testIdPrefix],
	);

	const badge = useCallback(
		() => (
			<ErrorBoundary
				id="notifications.badge.async"
				packageName="AtlassianNavigation"
				teamName="navigation"
				fallback="unmount"
			>
				<Placeholder name="badge" fallback={null}>
					<Badge
						isNotificationCleared={isNotificationCleared}
						setIsNotificationCleared={setIsNotificationCleared}
						isNotificationDrawerOpen={isMenuOpen}
						onNotificationCountChange={handleCountUpdate}
					/>
				</Placeholder>
			</ErrorBoundary>
		),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isNotificationCleared, isMenuOpen],
	);

	const badgeEmpty = useCallback(() => null, []);

	const notificationsLabel =
		notificationCount < MAX_NOTIFICATION_COUNT
			? formatMessage(messages.label, { count: notificationCount })
			: formatMessage(messages.overNineNotifications);

	const trigger = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'triggerProps' implicitly has an 'any' type.
		(triggerProps) => (
			<div
				data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
				data-vc="atlassian-navigation-notifications"
			>
				<Icon
					{...triggerProps}
					badge={badge}
					isSelected={isMenuOpen}
					onClick={onClick}
					onMouseEnter={handleNotificationIconHover}
					tooltip={formatMessage(messages.tooltip)}
					label={notificationsLabel}
					onViewRequests={openMenu}
				/>
			</div>
		),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[badge, isMenuOpen, onClick, formatMessage, testIdPrefix, notificationCount],
	);

	if (isAnonymous) return null;

	if (__SERVER__) return <Icon badge={badgeEmpty} tooltip={formatMessage(messages.tooltip)} />;

	// ENGHEALTH-39740: we need to wrap both the popup and trigger as 'listitem' since popup has role 'dialog' which cannot be a direct child of a 'list' parent
	// Any questions on this, reach out to dseong
	if (fg('blu-8308_restructure_notifications_button_a11y_fix')) {
		return (
			<div role="listitem">
				<Popup
					content={content}
					isOpen={isMenuOpen}
					onClose={closeMenu}
					placement="bottom-end"
					trigger={trigger}
					shouldRenderToParent
					shouldFitViewport={isMobileAndInViewPanelIterationExperiment() ? true : undefined}
					autoFocus={false}
				/>
			</div>
		);
	}

	return (
		<Popup
			content={content}
			isOpen={isMenuOpen}
			onClose={closeMenu}
			placement="bottom-end"
			trigger={trigger}
			shouldRenderToParent
			shouldFitViewport={isMobileAndInViewPanelIterationExperiment() ? true : undefined}
		/>
	);
};

const NotificationWithErrorBoundary = (props: NotificationsProps): JSX.Element => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.NOTIFICATIONS}>
		<UsingNotificationsToImproveAdminApprovalProvider>
			<Notifications {...props} />
		</UsingNotificationsToImproveAdminApprovalProvider>
	</TopLevelErrorBoundary>
);

export { NotificationWithErrorBoundary as Notifications };

const notificationContentFrameStyles = css({
	// accounts for the 5px padding added each side by the Popup component
	width: 'calc(100vw - 10px)',
	height: 'calc(100vh - 100px)',
	display: 'flex',
	[media.above.xs]: {
		width: '540px',
	},
});

const notificationContentFrameStylesUpdatedHeight = css({
	// accounts for the 5px padding added each side by the Popup component
	width: 'calc(100vw - 10px)',
	height: 'calc(100vh - 128px)',
	display: 'flex',
	[media.above.xs]: {
		width: '540px',
	},
});

const NotificationContentFrameDIWithResponsiveWidthUpdatedHeight = ({
	children,
}: {
	children: ReactNode;
}) => {
	return <div css={notificationContentFrameStylesUpdatedHeight}>{children}</div>;
};

const NotificationContentFrameDIWithResponsiveWidthLongHeight = ({
	children,
}: {
	children: ReactNode;
}) => {
	return <div css={notificationContentFrameStyles}>{children}</div>;
};

const NotificationContentFrameDIWithResponsiveWidth = componentWithFG(
	'jira-jjj-adjust-notifs-panel-height',
	NotificationContentFrameDIWithResponsiveWidthUpdatedHeight,
	NotificationContentFrameDIWithResponsiveWidthLongHeight,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order -- To migrate as part of go/ui-styling-standard
const NotificationContentFrameDIOldLongHeight = styled.div<{ children: ReactNode }>({
	width: '540px',
	height: 'calc(100vh - 100px)',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, jira/styled/styled-component-order -- To migrate as part of go/ui-styling-standard
const NotificationContentFrameDIOldUpdatedHeight = styled.div<{ children: ReactNode }>({
	width: '540px',
	height: 'calc(100vh - 128px)',
	display: 'flex',
});

const NotificationContentFrameDIOld = componentWithFG(
	'jira-jjj-adjust-notifs-panel-height',
	NotificationContentFrameDIOldUpdatedHeight,
	NotificationContentFrameDIOldLongHeight,
);

// TODO: When the cleanup is done, remove the exported wrapper component below please (see NotificationsContentWrapper)
//       Add fix the import in the Nav4Notifications componponent (which is colocated)
const NotificationContentFrameDI = componentWithCondition(
	() => isMobileAndInViewPanelIterationExperiment(),
	NotificationContentFrameDIWithResponsiveWidth,
	NotificationContentFrameDIOld,
);

// This has been created to circumvent a ratcheting rule and avoid an ESLint suppression
// for exporting components with styles. But this avoids duplicating code, and keeps bundle size down.
// Once the isMobileAndInViewPanelIterationExperiment() condition is removed, this can be removed.
export const NotificationsContentWrapper = ({ children }: { children: ReactNode }): JSX.Element => {
	return <NotificationContentFrameDI>{children}</NotificationContentFrameDI>;
};
